<template>
    <div class="head">
        <div class="title">{{ viewTitle }}</div>
        <GoBackButton @click.prevent="onGoBackButtonClick" />
        <LanguageSelector v-if="kiosk.isKioskMode" />
    </div>

    <div class="scrollable">
        <div v-if="isLoading" class="loading">
            <div class="loader"></div>
            {{ trans.receipt.loading }}
        </div>
        <template v-if="!isLoading && order != null">
            <div class="restaurant">{{ locationName }}</div>
            <div class="date">{{ orderDate }}</div>

            <div ref="itemsElement" class="items"></div>

            <OrderItemsList
                v-if="order != null"
                :orderItems="order.items"
                :showInternalNames="false"
                :isWaiterOrAdmin="isUserWaiterOrAdmin"
                :alwaysShowGroupName="true"
            />

            <div v-if="order != null" class="total">
                <div v-if="order.currentOrderDiscountAmount != 0" class="row">
                    <div class="lbl">{{ translations.pay.discount }}</div>
                    <div class="unit" style="color: #fd397a">{{ translations.currency.chf }}</div>
                    <div class="amt discount" style="color: #fd397a">
                        {{ toMoney(-1 * order.currentOrderDiscountAmount, true) }}
                    </div>
                </div>
                <div class="row">
                    <div class="lbl">{{ translations.pay.consumption }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(order.totalNetAmountFromPaymentsDrafts, true) }}</div>
                </div>
                <div class="row">
                    <div class="lbl">{{ translations.pay.fees }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(order.deliveryFee, true) }}</div>
                </div>
                <div v-if="order.tip !== 0" class="row">
                    <div class="lbl">{{ translations.pay.tip }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(order.tip, true) }}</div>
                </div>
                <div class="row total">
                    <div class="lbl">{{ trans.receipt.total }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(order.totalPaymentAmountFromPaymentDrafts, true) }}</div>
                </div>
            </div>
        </template>
    </div>

    <div class="btn-container">
        <div v-if="isCancelButtonVisible" class="btn cancelbtn" @click="cancelOrder">{{ trans.order.cancel }}</div>
        <div v-if="!isSendReceiptButtonHidden" class="btn sendreceiptbtn" @click="sendReceipt">
            {{ trans.receipt.receipt }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { ReceiptController } from "@/app/controller/ReceiptController";
    import { trans } from "@/app/localization/Trans";
    import { CancelWholeOrderPopover } from "@/app/popover/CancelOrderPopover";
    import { SendOrderPaymentReceiptPopover } from "@/app/popover/SendOrderPaymentReceiptPopover";
    import { Popup } from "@/app/ui/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { ICustomerOrder, IFullCustomerOrder } from "@/data/model/Customer";
    import { OrderState } from "@/data/model/Enums";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { Ripple } from "@/shared/ui/Ripple";
    import { toMoney } from "@/shared/utils/format";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { computed, onMounted, onUnmounted, ref } from "vue";

    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import OrderItemsList from "@/components/order/OrderItemsList.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";
    const kiosk = useKioskStore();

    const tableAllocation = useTableAllocationStore();
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const props = defineProps<{
        data: ICustomerOrder;
    }>();

    function onGoBackButtonClick() {
        ReceiptController.onGoBack.dispatch();
    }

    // header
    const viewTitle = computed(() =>
        orderSummary.value == null
            ? trans.receipt.title
            : `${trans.receipt.title} ${orderSummary.value.dailyOrderIncrement ?? orderSummary.value.orderNumber}`
    );

    // state
    const orderSummary = ref<ICustomerOrder | null>(null);
    const order = ref<IFullCustomerOrder | null>(null);
    const canOrderBeCanceled = ref<boolean>(false);

    const isLoading = ref(false);
    const isUserWaiterOrAdmin = ref<boolean>(false);

    // html refs
    const cancelButtonElement = ref<HTMLDivElement | null>(null);
    const sendReceiptButtonElement = ref<HTMLDivElement | null>(null);

    // computed
    const locationName = computed(() => order.value?.locationName);
    const orderDate = computed(() =>
        order.value ? `${DateTime.toDateString(order.value!.date)}, ${DateTime.toTimeString(order.value!.date)}` : null
    );
    const isSendReceiptButtonHidden = computed(
        () =>
            order.value == null ||
            order.value.payLater ||
            order.value.state === OrderState.NotPaid ||
            order.value.consumption <= 0
    );
    const isCancelButtonVisible = computed(
        () =>
            order.value != null &&
            order.value.cancellationState == null &&
            canOrderBeCanceled.value
    );

    // functions
    async function loadOrder() {
        if (!orderSummary.value) return;

        isUserWaiterOrAdmin.value = TastierClient.identityContext.isWaiterOrAdmin();

        try {
            isLoading.value = true;
            order.value = await customerDao.getOrder(orderSummary.value!.id);
            isLoading.value = false;
            canOrderBeCanceled.value = await checkCanOrderBeCanceled();
        } catch (ex) {
            logError(ex);
            isLoading.value = false;

            Popup.show("Failed to load order.", () => {
                ReceiptController.onGoBack.dispatch();
            });
        }
    }

    async function checkCanOrderBeCanceled(): Promise<boolean> {
        try {
            const orderId = order.value!.id;
            return await customerDao.getCanOrderBeCanceled(orderId, {
                locationId: tableAllocation.locationId,
                orderId: orderId,
            });
        } catch (error) {
            logError(error);
        }

        return false;
    }

    function cancelOrder() {
        const popover = new CancelWholeOrderPopover(order.value!.id, tableAllocation.locationId!, {
            uniqueId: "cancel-order-popover",
            onOrderCancellationSucceeded: () => {
                Popup.show(
                    trans.order.cancellationSuccessful,
                    () => {
                        loadOrder();
                        popover.hide();
                    },
                    trans.general.ok,
                    undefined,
                    "cancellation-succeeded-popup"
                );
            },
            onOrderCancellationFailed: (errorMessage: string) => {
                Popup.show(
                    errorMessage,
                    () => {
                        loadOrder();
                        popover.hide();
                    },
                    trans.general.ok,
                    undefined,
                    "cancellation-failed-popup"
                );
            },
        });
        popover.show();
    }

    function sendReceipt() {
        const popover = new SendOrderPaymentReceiptPopover(order.value!.id, order.value!.locationId);
        popover.show();
    }

    onMounted(() => {
        // todo: this doesn't work, because buttons might not be visible yet - refactor to apply after buttons are visible
        Ripple.bind($(cancelButtonElement.value!));
        Ripple.bind($(sendReceiptButtonElement.value!));

        orderSummary.value = props.data;
        loadOrder();
    });

    onUnmounted(() => ReceiptController.onViewUnmounted.dispatch());
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .receipt {
        > .scrollable {
            position: absolute;
            top: 53px;
            left: 0;
            bottom: 0;
            right: 0;
            padding: 24px 24px 134px 24px;

            > .restaurant {
                font-size: 16px;
                line-height: 20px;
                color: colors.$fontColor;
                font-weight: 600;
                padding: 6px 0;
            }

            > .date {
                font-size: 14px;
                font-weight: 300;
                color: #aaaaaa;
                line-height: 16px;
                padding-bottom: 6px;
            }

            > .loading {
                margin-top: 60px;
                position: absolute;
                left: 50%;
                white-space: nowrap;
                transform: translateX(-50%);

                > .loader {
                    background-image: url("/assets/img/app/loading_green.svg");
                }
            }

            > .total {
                padding-top: 12px;

                > .row {
                    font-size: 14px;
                    color: colors.$fontColor;
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    justify-content: space-between;
                    line-height: 24px;

                    &.total {
                        padding-top: 8px;
                    }

                    > .lbl {
                        flex-grow: 1;
                        display: flex;
                        white-space: nowrap;
                    }

                    .total-container {
                        flex-grow: 1;
                        display: flex;

                        > .lbl {
                            white-space: nowrap;
                            font-weight: 600;
                        }

                        > .ccinfo {
                            line-height: 22px;
                            white-space: nowrap;
                            overflow: hidden;
                            font-size: 12px;

                            .brandicon {
                                display: inline-block;
                                vertical-align: middle;
                                width: 30px;
                                height: 22px;
                                margin-left: 8px;
                                background-size: contain;
                                background-repeat: no-repeat;
                                background-position: center center;
                            }
                        }
                    }

                    > .unit {
                        flex: 0 0 30px;
                        text-align: right;

                        &.total {
                            font-weight: 600;
                        }
                    }

                    > .amt {
                        flex: 0 0 50px;
                        text-align: right;
                        padding-left: 6px;
                        white-space: nowrap;

                        &.total {
                            font-weight: 600;
                        }
                    }
                }
            }
        }

        > .btn-container {
            padding: 14px 28px 14px 28px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            flex-direction: column-reverse;

            > .sendreceiptbtn {
                background-color: var(--colorPrimary);
                color: #fff;
                border-radius: 23px;
                text-align: center;
                font-size: 17px;
                line-height: 46px;
                cursor: pointer;

                @include mixins.hover-and-active() {
                    background-color: var(--colorPrimaryHover);
                }
            }

            > .cancelbtn {
                background-color: colors.$red;
                color: #fff;
                margin-top: 14px;
                border-radius: 23px;
                text-align: center;
                font-size: 17px;
                line-height: 46px;
                cursor: pointer;

                @include mixins.hover-and-active() {
                    background-color: colors.$redHover;
                }
            }
        }
    }

    // kiosk: the view is px throughout. the scrollable's bottom padding is the exact height
    // of the button container below it (14 + 46 + 14 + 46 + 14 = 134), so the two have to
    // scale together. the scrollable's top sits with the other view offsets in
    // KioskModeConfiguration.vue
    .kiosk-mode .app > .receipt {
        > .scrollable {
            padding: (24px * variables.$kioskScale) (24px * variables.$kioskScale)
                (134px * variables.$kioskScale) (24px * variables.$kioskScale);

            > .restaurant {
                font-size: 16px * variables.$kioskScale;
                line-height: 20px * variables.$kioskScale;
                padding: (6px * variables.$kioskScale) 0;
            }

            > .date {
                font-size: 14px * variables.$kioskScale;
                line-height: 16px * variables.$kioskScale;
                padding-bottom: 6px * variables.$kioskScale;
            }

            > .loading {
                margin-top: 60px * variables.$kioskScale;
            }

            > .total {
                padding-top: 12px * variables.$kioskScale;

                > .row {
                    font-size: 14px * variables.$kioskScale;
                    line-height: 24px * variables.$kioskScale;

                    &.total {
                        padding-top: 8px * variables.$kioskScale;
                    }

                    .total-container > .ccinfo {
                        line-height: 22px * variables.$kioskScale;
                        font-size: 12px * variables.$kioskScale;

                        .brandicon {
                            width: 30px * variables.$kioskScale;
                            height: 22px * variables.$kioskScale;
                            margin-left: 8px * variables.$kioskScale;
                        }
                    }

                    // the amount columns are fixed-width, so they have to grow with the text
                    > .unit {
                        flex: 0 0 (30px * variables.$kioskScale);
                    }

                    > .amt {
                        flex: 0 0 (50px * variables.$kioskScale);
                        padding-left: 6px * variables.$kioskScale;
                    }
                }
            }
        }

        > .btn-container {
            padding: (14px * variables.$kioskScale) (28px * variables.$kioskScale);

            // the buttons take their height from line-height, there is no height property
            > .sendreceiptbtn,
            > .cancelbtn {
                border-radius: 23px * variables.$kioskScale;
                font-size: 17px * variables.$kioskScale;
                line-height: 46px * variables.$kioskScale;
            }

            > .cancelbtn {
                margin-top: 14px * variables.$kioskScale;
            }
        }
    }
</style>
